import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { Text } from "@redotech/arbiter/text";
import { useHandler } from "@redotech/react-util/hook";
import ArrowLeft from "@redotech/redo-icons/arrow-left.svg";
import { nativeRandom, randomHexString } from "@redotech/util/random";
import { memo, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useRouteError } from "react-router-dom";
import { CopyIconButton } from "../copy-icon-button";
import Logo from "../logo.svg";
import * as css from "./not-found-page.module.css";
import { isNotFoundRouteError } from "./not-found-route-error";
import voidHeroUrl from "./void-hero.webp?url";

export interface NotFoundPageProps {
  errorId?: string;
}

export const NotFoundPage = memo(function NotFoundPage({
  errorId,
}: NotFoundPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const error = useRouteError();
  const isNotFound = isNotFoundRouteError(error);
  const generatedErrorId = useMemo(() => randomHexString(nativeRandom, 8), []);
  const resolvedErrorId = errorId ?? generatedErrorId;
  const subtitle = isNotFound
    ? "404: Can't find this page"
    : "Error: An unexpected error occurred.";

  useEffect(() => {
    if (error) {
      console.error(`An error occurred with ID ${resolvedErrorId}:`, error);
    }
    if (isNotFound) {
      console.error(
        `Route not found at location with error id ${resolvedErrorId}:`,
        location.pathname,
      );
    }
  }, [error, isNotFound, location.pathname, resolvedErrorId]);

  const handleGoBack = useHandler(() => {
    navigate(-1);
  });

  return (
    <Flex gap="none" minH="screen" position="relative">
      <Flex bgColor="primary" dir="column" flex={1} gap="none" minH="screen">
        <Flex alignSelf="flex-start" className={css.logoContainer} gap="none">
          <a href="https://www.redo.com" rel="noreferrer" target="_blank">
            <Logo className={css.logo} />
          </a>
        </Flex>
        <Flex
          align="center"
          className={css.contentWrapper}
          flex={1}
          gap="none"
          justify="center"
        >
          <Flex
            as="section"
            className={css.content}
            dir="column"
            gap="3xl"
            w="full"
          >
            <Flex align="center" dir="column" gap="xs" textAlign="center">
              <Text
                as="h1"
                fontFamily="serif"
                fontSize="5xl"
                lineHeight="1"
                textColor="primary"
              >
                Lost in the void
              </Text>
              <Text fontSize="sm" textColor="tertiary">
                {subtitle}
              </Text>
            </Flex>
            <Flex dir="column" gap="xs" textAlign="left" w="full">
              <Text fontSize="xs" textColor="tertiary">
                Error ID
              </Text>
              <Flex
                align="center"
                bgColor="secondary"
                borderColor="primary"
                borderRadius="sm"
                borderStyle="solid"
                borderWidth="1px"
                gap="sm"
                justify="space-between"
                px="lg"
                py="md"
                w="full"
              >
                <Text fontFamily="monospace" fontSize="sm" textColor="primary">
                  {resolvedErrorId}
                </Text>
                <CopyIconButton
                  textToCopy={resolvedErrorId}
                  tooltipLabel="error ID"
                />
              </Flex>
            </Flex>
            <RedoButton
              className={css.fullWidthButton}
              hierarchy="primary"
              IconLeading={ArrowLeft}
              onClick={handleGoBack}
              size="sm"
              text="Go back"
            />
          </Flex>
        </Flex>
      </Flex>
      <Flex
        className={css.panel}
        dir="column"
        gap="none"
        justify="flex-end"
        overflow="hidden"
        position="relative"
      >
        <img alt="" aria-hidden className={css.heroImage} src={voidHeroUrl} />
      </Flex>
    </Flex>
  );
});
